import { NextIntlClientProvider } from "next-intl";
import { createContext, useContext, useEffect, useState, type ReactNode } from "react";

const DEFAULT_LOCALE = "en-GB";

// Locales bundled in ./messages/*.json — full BCP-47 codes (e.g. "de-DE").
// Enumerated at build time so locale resolution can validate candidates
// against what's actually shippable.
const SUPPORTED_LOCALES: string[] = Object.keys(
  import.meta.glob("./messages/*.json"),
).map((p) => p.match(/\/([^/]+)\.json$/)![1]!);

/**
 * Opinionated base-language → canonical full locale. PORTED from the platform's
 * single source of truth (main-frontend/src/lib/utils/locale-normalize.ts
 * LEGACY_LANG_MAP) — the portal has no shared package, so this is a hand-kept
 * copy. Keep aligned. Guarantees a bare "de" resolves to de-DE (not the
 * alphabetically-first de-BE), matching guests/templates everywhere else.
 * Mapped values that aren't among the 57 bundled portal locales simply fail the
 * SUPPORTED check below and fall through.
 */
const LEGACY_LANG_MAP: Record<string, string> = {
  af: "af-ZA", sq: "sq-AL", am: "am-ET", ar: "ar-SA", hy: "hy-AM",
  az: "az-AZ", eu: "eu-ES", be: "be-BY", bn: "bn-BD", bs: "bs-BA",
  bg: "bg-BG", my: "my-MM", ca: "ca-ES", zh: "zh-CN", hr: "hr-HR",
  cs: "cs-CZ", da: "da-DK", nl: "nl-NL", en: "en-GB", et: "et-EE",
  fi: "fi-FI", fr: "fr-FR", ka: "ka-GE", de: "de-DE", el: "el-GR",
  gu: "gu-IN", ha: "ha-NG", he: "he-IL", hi: "hi-IN", hu: "hu-HU",
  is: "is-IS", id: "id-ID", ga: "ga-IE", it: "it-IT", ja: "ja-JP",
  kn: "kn-IN", kk: "kk-KZ", km: "km-KH", ko: "ko-KR", ky: "ky-KG",
  lo: "lo-LA", lv: "lv-LV", lt: "lt-LT", lb: "lb-LU", mk: "mk-MK",
  ms: "ms-MY", ml: "ml-IN", mt: "mt-MT", mr: "mr-IN", mn: "mn-MN",
  ne: "ne-NP", nb: "nb-NO", no: "nb-NO", fa: "fa-IR", pl: "pl-PL",
  pt: "pt-PT", pa: "pa-IN", ro: "ro-RO", ru: "ru-RU", sr: "sr-RS",
  si: "si-LK", sk: "sk-SK", sl: "sl-SI", so: "so-SO", es: "es-ES",
  sw: "sw-KE", sv: "sv-SE", tl: "tl-PH", ta: "ta-IN", te: "te-IN",
  th: "th-TH", tr: "tr-TR", uk: "uk-UA", ur: "ur-PK", uz: "uz-UZ",
  vi: "vi-VN", cy: "cy-GB", yo: "yo-NG", zu: "zu-ZA",
};

/**
 * Pure resolver (exported for tests): map an arbitrary language candidate to a
 * member of `supported`, mirroring the platform's normalizeToFullLocale
 * contract — case-insensitive exact match → opinionated base map → any locale
 * sharing the base language. Returns null when nothing matches so callers fall
 * through to the next preference.
 */
export function resolveAgainst(
  supported: readonly string[],
  candidate?: string | null,
): string | null {
  if (typeof candidate !== "string") return null;
  const c = candidate.trim();
  if (c.length === 0) return null;
  // Exact, case-insensitive — return the canonically-cased bundle name so the
  // dynamic import path matches the file on disk.
  const exact = supported.find((l) => l.toLowerCase() === c.toLowerCase());
  if (exact) return exact;
  const base = c.toLowerCase().split("-")[0]!;
  const mapped = LEGACY_LANG_MAP[base];
  if (mapped && supported.includes(mapped)) return mapped;
  // Last resort: any bundled locale sharing the base language.
  return supported.find((l) => l.toLowerCase().startsWith(`${base}-`)) ?? null;
}

/**
 * Resolve an arbitrary language candidate (a guest's stored language or the
 * event's defaultLocale — either a full "de-DE" or a short "de") to a locale
 * we actually bundle.
 */
export function resolveSupportedLocale(candidate?: string | null): string | null {
  return resolveAgainst(SUPPORTED_LOCALES, candidate);
}

type LocaleContextValue = {
  locale: string;
  setLocale: (loc: string) => void;
};

const LocaleContext = createContext<LocaleContextValue>({
  locale: DEFAULT_LOCALE,
  setLocale: () => undefined,
});

export function useLocale() {
  return useContext(LocaleContext);
}

/**
 * Detect locale from <html lang>, navigator.language, or fall back.
 * Once the API response arrives the caller can switch via setLocale.
 */
function detectInitialLocale(): string {
  const htmlLang = document.documentElement.lang;
  if (htmlLang && /^[a-z]{2}-[A-Z]{2}$/.test(htmlLang)) return htmlLang;
  const nav = navigator.language;
  if (nav && /^[a-z]{2}-[A-Z]{2}$/.test(nav)) return nav;
  return DEFAULT_LOCALE;
}

export function I18nProvider({ children }: { children: ReactNode }) {
  const [locale, setLocale] = useState(detectInitialLocale);
  const [messages, setMessages] = useState<Record<string, unknown> | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const mod = await import(`./messages/${locale}.json`).catch(() =>
        import(`./messages/${DEFAULT_LOCALE}.json`),
      );
      if (!cancelled) setMessages(mod.default);
    })();
    return () => {
      cancelled = true;
    };
  }, [locale]);

  if (!messages) return null;

  return (
    <LocaleContext.Provider value={{ locale, setLocale }}>
      <NextIntlClientProvider locale={locale} messages={messages}>
        {children}
      </NextIntlClientProvider>
    </LocaleContext.Provider>
  );
}
